import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Heart,
  Baby,
  DollarSign,
  Smile,
  ArrowLeft,
  Phone,
  MessageSquare,
  Calendar,
  MapPin,
  ExternalLink,
  Shield,
  Users,
  BookOpen
} from 'lucide-react';

export function LifePlatform() {
  const services = {
    childcare: [
      {
        name: 'Local Nurseries Directory',
        description: 'Find registered childcare providers near you',
        icon: Baby,
        type: 'Directory'
      },
      {
        name: 'Childcare Support Grant',
        description: 'Information on financial assistance for childcare costs',
        icon: DollarSign,
        type: 'Financial'
      },
      {
        name: 'After-School Clubs',
        description: 'Flexible care options for school-age children',
        icon: Users,
        type: 'Service'
      },
    ],
    wellbeing: [
      {
        name: 'Mental Health Support',
        description: '24/7 confidential helpline and counseling services',
        icon: Heart,
        type: 'Support'
      },
      {
        name: 'Stress Management Resources',
        description: 'Tools and techniques for managing work-life balance',
        icon: Smile,
        type: 'Resources'
      },
      {
        name: 'Employee Assistance Program',
        description: 'Free counseling and support services',
        icon: Shield,
        type: 'Program'
      },
    ],
    finance: [
      {
        name: 'Financial Planning Advice',
        description: 'Free consultations with qualified advisors',
        icon: DollarSign,
        type: 'Advice'
      },
      {
        name: 'Benefits Calculator',
        description: 'Check what support you might be entitled to',
        icon: Calculator,
        type: 'Tool'
      },
      {
        name: 'Debt Management Support',
        description: 'Confidential help with managing finances',
        icon: Shield,
        type: 'Support'
      },
    ],
    support: [
      {
        name: 'Career Coaching',
        description: 'One-on-one guidance for your career development',
        icon: BookOpen,
        type: 'Coaching'
      },
      {
        name: 'Returner Support Groups',
        description: 'Connect with others returning to work',
        icon: Users,
        type: 'Community'
      },
      {
        name: 'Skills Workshops',
        description: 'Free training to boost your confidence',
        icon: BookOpen,
        type: 'Training'
      },
    ],
  };

  const urgentContacts = [
    {
      service: 'Mental Health Crisis',
      number: '0800 123 4567',
      hours: '24/7',
      description: 'Immediate support for mental health emergencies'
    },
    {
      service: 'Childcare Emergency',
      number: '0800 234 5678',
      hours: 'Mon-Fri, 8am-8pm',
      description: 'Emergency childcare placement assistance'
    },
    {
      service: 'Financial Crisis Support',
      number: '0800 345 6789',
      hours: '24/7',
      description: 'Urgent financial advice and support'
    },
  ];

  const resources = [
    {
      title: 'Returning to Work Guide',
      description: 'Complete guide for parents and carers returning to employment',
      category: 'Guide',
      readTime: '15 min'
    },
    {
      title: 'Work-Life Balance Tips',
      description: 'Practical advice for managing career and personal life',
      category: 'Article',
      readTime: '8 min'
    },
    {
      title: 'Financial Wellbeing Checklist',
      description: 'Steps to improve your financial health',
      category: 'Checklist',
      readTime: '10 min'
    },
    {
      title: 'Childcare Options Explained',
      description: 'Understanding different types of childcare support',
      category: 'Guide',
      readTime: '12 min'
    },
  ];

  return (
    <div className="relative min-h-screen pt-16 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]" />
      {/* Header */}
      <header className="border-b border-white/10 bg-gradient-to-b from-white/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex w-16 h-16 rounded-2xl bg-gold-gradient items-center justify-center mb-6">
              <Heart className="w-8 h-8 text-black" />
            </div>
            <h1 className="mb-4 text-gold-gradient">
              Life Platform
            </h1>
            <p className="text-gray-400 mb-8">
              Comprehensive support for your personal wellbeing, childcare, and financial health
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Urgent Contacts Banner */}
        <Card className="bg-gradient-to-br from-red-500/10 to-transparent border-red-500/20 mb-12">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Phone className="w-5 h-5 text-red-400" />
              Urgent Support Contacts
            </CardTitle>
            <CardDescription className="text-gray-400">
              24/7 helplines for immediate assistance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {urgentContacts.map((contact, index) => (
                <div
                  key={index}
                  className="p-4 bg-white/5 rounded-lg border border-white/10"
                >
                  <h4 className="text-white mb-2">{contact.service}</h4>
                  <p className="text-2xl text-gold-gradient mb-2">
                    {contact.number}
                  </p>
                  <p className="text-sm text-gray-400 mb-2">{contact.hours}</p>
                  <p className="text-xs text-gray-500">{contact.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Services Tabs */}
        <Tabs defaultValue="childcare" className="mb-12">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 bg-white/5 border border-white/10 mb-8">
            <TabsTrigger value="childcare" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <Baby className="w-4 h-4 mr-2" />
              Childcare
            </TabsTrigger>
            <TabsTrigger value="wellbeing" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <Heart className="w-4 h-4 mr-2" />
              Wellbeing
            </TabsTrigger>
            <TabsTrigger value="finance" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <DollarSign className="w-4 h-4 mr-2" />
              Finance
            </TabsTrigger>
            <TabsTrigger value="support" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <Users className="w-4 h-4 mr-2" />
              Support
            </TabsTrigger>
          </TabsList>

          {Object.entries(services).map(([key, serviceList]) => (
            <TabsContent key={key} value={key}>
              <div className="grid md:grid-cols-3 gap-6">
                {serviceList.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <Card
                      key={index}
                      className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300 group cursor-pointer"
                    >
                      <CardHeader>
                        <div className="w-12 h-12 rounded-lg bg-gold-gradient flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                          <Icon className="w-6 h-6 text-black" />
                        </div>
                        <CardTitle className="text-white">{service.name}</CardTitle>
                        <CardDescription className="text-gray-400">
                          {service.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#ba9036]">{service.type}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/5"
                          >
                            Learn More
                            <ExternalLink className="w-3 h-3 ml-2" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Returner Hub */}
        <section className="mb-12">
          <h2 className="mb-8 text-center text-gold-gradient">
            Returner Hub
          </h2>
          <Card className="bg-gradient-to-br from-[#edc967]/10 to-transparent border-[#d2ac47]/20">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-white mb-4">Designed for Your Return to Work</h3>
                  <p className="text-gray-300 mb-6">
                    Whether you're a parent, carer, or returning after long-term unemployment, 
                    we provide specialized support to help you successfully transition back to work.
                  </p>
                  <ul className="space-y-3 mb-6">
                    {[
                      'Flexible job opportunities',
                      'Skills refresher workshops',
                      'Confidence-building programs',
                      'Peer support groups',
                      'Family-friendly employers'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-gray-300">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Shield className="w-3 h-3 text-black" />
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Button className="bg-gold-gradient hover:brightness-110 text-black">
                    Join Returner Hub
                  </Button>
                </div>
                <div className="space-y-4">
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-[#ba9036] flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="text-white mb-1">Next Workshop</h4>
                          <p className="text-sm text-gray-400 mb-2">Skills Refresher for Care Workers</p>
                          <p className="text-xs text-gray-500">November 15, 2025 • 10:00 AM</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-[#ba9036] flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="text-white mb-1">Support Group</h4>
                          <p className="text-sm text-gray-400 mb-2">Parents Returning to Work</p>
                          <p className="text-xs text-gray-500">Weekly meetings • Online & In-person</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-[#ba9036] flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="text-white mb-1">One-on-One Coaching</h4>
                          <p className="text-sm text-gray-400 mb-2">Free career guidance sessions</p>
                          <p className="text-xs text-gray-500">Book your session today</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Resources */}
        <section>
          <h2 className="mb-8 text-center text-gold-gradient">
            Helpful Resources
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {resources.map((resource, index) => (
              <Card
                key={index}
                className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300 cursor-pointer"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-1 rounded bg-[#ba9036]/20 text-[#ba9036] border border-[#d2ac47]/50">
                          {resource.category}
                        </span>
                        <span className="text-xs text-gray-500">{resource.readTime} read</span>
                      </div>
                      <h4 className="text-white mb-2">{resource.title}</h4>
                      <p className="text-sm text-gray-400">{resource.description}</p>
                    </div>
                    <BookOpen className="w-5 h-5 text-[#ba9036] flex-shrink-0 ml-4" />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/5"
                  >
                    Read More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function Calculator({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="16" y1="14" x2="16" y2="14" />
      <line x1="8" y1="14" x2="8" y2="14" />
      <line x1="12" y1="14" x2="12" y2="14" />
      <line x1="8" y1="18" x2="8" y2="18" />
      <line x1="12" y1="18" x2="12" y2="18" />
      <line x1="16" y1="18" x2="16" y2="18" />
    </svg>
  );
}
